import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';

const BLOCK_MSG = 'Exports, downloads and printing are disabled in this restricted demo.';
const EXPORT_TESTID = /(export|download|pdf|csv|xlsx|print)/i;
const EXPORT_TEXT = /\b(export|download|pdf|csv|xlsx|print)\b/i;

export const isCaptureShortcut = (e) => {
  if (e.key === 'PrintScreen' || e.code === 'PrintScreen') return true;
  const mod = !!(e.metaKey || e.ctrlKey);
  if (mod && e.shiftKey && ['Digit3', 'Digit4', 'Digit5', 'KeyS'].includes(e.code)) return true;
  return mod && ['KeyP', 'KeyS'].includes(e.code);
};

export const isExportControl = (target) => {
  const el = target?.closest?.('button, a, [role="menuitem"]');
  if (!el) return false;
  if (el.tagName === 'A' && el.hasAttribute('download')) return true;
  if (EXPORT_TESTID.test(el.getAttribute('data-testid') || '')) return true;
  const text = (el.textContent || '').trim();
  return el.tagName !== 'A' && text.length <= 40 && EXPORT_TEXT.test(text);
};

const blockedToast = () => toast.error(BLOCK_MSG, { id: 'restricted-demo-block' });

// Patches every download/print path; returns a restore fn.
const patchDownloads = () => {
  const { click, dispatchEvent } = HTMLAnchorElement.prototype;
  const print = window.print;
  HTMLAnchorElement.prototype.click = function demoClick() {
    if (this.hasAttribute('download')) return blockedToast();
    return click.call(this);
  };
  HTMLAnchorElement.prototype.dispatchEvent = function demoDispatch(evt) {
    if (evt?.type === 'click' && this.hasAttribute('download')) { blockedToast(); return false; }
    return dispatchEvent.call(this, evt);
  };
  window.print = blockedToast;
  return () => {
    HTMLAnchorElement.prototype.click = click;
    HTMLAnchorElement.prototype.dispatchEvent = dispatchEvent;
    window.print = print;
  };
};

const useDemoGuards = (active, onCapture) => {
  useEffect(() => {
    if (!active) return undefined;
    const restore = patchDownloads();
    const onKey = (e) => {
      if (!isCaptureShortcut(e)) return;
      e.preventDefault();
      navigator.clipboard?.writeText?.('').catch(() => {});
      onCapture();
    };
    const onVisibility = () => { if (document.hidden) onCapture(); };
    const onBlur = (e) => e.target === window && setTimeout(() => {
      if (document.activeElement?.tagName !== 'IFRAME') onCapture();
    }, 0);
    const onClick = (e) => {
      if (!isExportControl(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      blockedToast();
    };
    const prevent = (e) => e.preventDefault();
    const listeners = [
      [window, 'keydown', onKey], [window, 'keyup', onKey], [window, 'blur', onBlur],
      [document, 'visibilitychange', onVisibility], [document, 'click', onClick], [document, 'pointerdown', onClick],
      [document, 'contextmenu', prevent], [document, 'copy', prevent],
      [document, 'cut', prevent], [document, 'dragstart', prevent],
    ];
    listeners.forEach(([t, ev, fn]) => t.addEventListener(ev, fn, true));
    document.body.classList.add('pfd-restricted-demo');
    return () => {
      listeners.forEach(([t, ev, fn]) => t.removeEventListener(ev, fn, true));
      document.body.classList.remove('pfd-restricted-demo');
      restore();
    };
  }, [active, onCapture]);
};

const DemoWatermark = ({ email }) => {
  const label = `${email} · Restricted demo · ${new Date().toISOString().slice(0, 10)}`;
  return (
    <div aria-hidden="true" data-testid="restricted-demo-watermark"
      className="pointer-events-none fixed inset-0 z-[9990] select-none overflow-hidden">
      <div className="absolute -inset-1/2 grid grid-cols-2 md:grid-cols-4 content-center gap-x-24 gap-y-24 -rotate-[24deg]">
        {Array.from({ length: 64 }).map((_, i) => (
          <span key={i} className="whitespace-nowrap text-sm font-semibold tracking-wide text-slate-900/[0.08] dark:text-white/[0.08]">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const CaptureShield = ({ email, onDismiss }) => (
  <div data-testid="restricted-demo-shield" role="alertdialog" aria-modal="true"
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 p-6 backdrop-blur-2xl">
    <div className="max-w-md rounded-2xl border border-white/10 bg-slate-900 p-7 text-center text-white shadow-2xl">
      <ShieldAlert className="mx-auto h-10 w-10 text-amber-400" />
      <h2 className="mt-4 text-lg font-semibold" data-testid="restricted-demo-shield-title">Screen capture is not permitted</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">
        This restricted demo is licensed to <span className="font-semibold text-white">{email}</span>.
        Screenshots, recordings, printing and exports are not allowed. Content stays hidden while the
        window is out of focus.
      </p>
      <Button onClick={onDismiss} data-testid="restricted-demo-shield-continue"
        className="mt-6 rounded-full bg-amber-400 px-6 text-slate-900 hover:bg-amber-300">
        Return to demo
      </Button>
    </div>
  </div>
);

export const RestrictedDemoShield = () => {
  const { user } = useAuth();
  const active = !!user?.is_restricted_demo;
  const [shielded, setShielded] = useState(false);
  const onCapture = useCallback(() => setShielded(true), []);
  useDemoGuards(active, onCapture);
  if (!active) return null;
  return (
    <>
      <DemoWatermark email={user.email} />
      {shielded && <CaptureShield email={user.email} onDismiss={() => setShielded(false)} />}
    </>
  );
};
